import { COUNTRY_OPTIONS } from './countries'
import {
  countryUsesStateSelect,
  getSubdivisionDisplayLabel,
  isKnownSubdivisionCode,
} from './subdivisions'

const COUNTRY_LABEL_MAP = new Map(
  COUNTRY_OPTIONS.map((opt) => [opt.value.toLowerCase(), opt.label]),
)

// Address type definition
export interface Address {
  firstName: string
  lastName: string
  street: string
  number: string
  postcode: string
  city: string
  state: string
  country: string
}

// Form field configuration for reusability.
// firstName and lastName are excluded — they are read-only and pre-populated
// from customerData, so they are not validated as user-editable inputs.
export const ADDRESS_FIELDS = [
  { name: 'street', label: 'Address line 1', type: 'text', required: true },
  { name: 'postcode', label: 'Postcode', type: 'text', required: true },
  { name: 'city', label: 'City', type: 'text', required: true },
  { name: 'state', label: 'State / Province', type: 'text', required: false },
  { name: 'country', label: 'Country', type: 'text', required: true },
] as const

// Empty address template
export const EMPTY_ADDRESS: Address = {
  firstName: '',
  lastName: '',
  street: '',
  number: '',
  postcode: '',
  city: '',
  state: '',
  country: '',
}

// Stable keys for the address card lines — used to attach `data-testid`
// attributes on the rendered spans so Playwright POMs can read each field
// independently.
export type AddressLineKey =
  | 'name'
  | 'street'
  | 'city-state-postcode'
  | 'country'

export interface AddressLineEntry {
  key: AddressLineKey
  text: string
}

// Format address for display - matches Figma layout. Returns labeled entries
// (each line tagged with its semantic `key`) so callers can render
// `data-testid={`address-card-${key}`}`.
export const formatAddressLineEntries = (
  address: Address,
): AddressLineEntry[] => {
  const entries: AddressLineEntry[] = []

  // Name line
  const name = [address.firstName, address.lastName].filter(Boolean).join(' ')
  if (name) entries.push({ key: 'name', text: name })

  // Street line — only append number if it doesn't already appear as a
  // distinct token in the street string (avoids "Kinkerstraat 67-5 67-5").
  // Uses whitespace/token boundaries so partial matches like "5" inside "52" are safe.
  const numberDuplicated =
    !!address.number &&
    new RegExp(
      `(?:^|\\s)${address.number.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s|$)`,
    ).test(address.street)
  const streetLine = [address.street, numberDuplicated ? '' : address.number]
    .filter(Boolean)
    .join(' ')
  if (streetLine) entries.push({ key: 'street', text: streetLine })

  // City, State, Postcode line (US/CA: show full subdivision name when code is known)
  const stateLine =
    getSubdivisionDisplayLabel(address.country, address.state) ?? address.state
  const cityParts = [address.city, stateLine, address.postcode]
    .filter(Boolean)
    .join(', ')
  if (cityParts) entries.push({ key: 'city-state-postcode', text: cityParts })

  // Country — show full name, fall back to code if not found
  if (address.country) {
    const countryLabel =
      COUNTRY_LABEL_MAP.get(address.country.toLowerCase()) ?? address.country
    entries.push({ key: 'country', text: countryLabel })
  }

  return entries
}

// Backwards-compatible flat-string view, used by callers that don't need the
// labeled keys (e.g. unit tests).
export const formatAddressLines = (address: Address): string[] =>
  formatAddressLineEntries(address).map((entry) => entry.text)

// Check if address has any data
export const hasAddressData = (address?: Address): boolean => {
  if (!address) return false
  return Object.values(address).some(
    (value) => typeof value === 'string' && value.trim() !== '',
  )
}

// Remove a single key from an errors record, returning the same reference if unchanged
export const clearFieldError = (
  errors: Record<string, string>,
  field: string,
): Record<string, string> => {
  if (!errors[field]) return errors
  return Object.fromEntries(
    Object.entries(errors).filter(([key]) => key !== field),
  )
}

const ADDRESS_FIELD_ERROR_KEYS: Partial<Record<string, string>> = {
  street: 'addressBook.validation.addressLine1Required',
  postcode: 'addressBook.validation.postcodeRequired',
  city: 'addressBook.validation.cityRequired',
  country: 'addressBook.validation.countryRequired',
}

// Validate address fields. Requires a translation function so error messages
// are localised; pass `t` from `useI18N()` at the call site.
export const validateAddress = (
  address: Address,
  t: (key: string) => string,
): Record<string, string> => {
  const errors: Record<string, string> = {}

  ADDRESS_FIELDS.forEach((field) => {
    if (field.required && !address[field.name as keyof Address].trim()) {
      const key = ADDRESS_FIELD_ERROR_KEYS[field.name]
      errors[field.name] = key ? t(key) : `${field.label} is required`
    }
  })

  if (countryUsesStateSelect(address.country)) {
    if (!address.state.trim()) {
      errors.state = t('addressBook.validation.stateRequired')
    } else if (!isKnownSubdivisionCode(address.country, address.state)) {
      errors.state = t('addressBook.validation.invalidState')
    }
  }

  return errors
}
